import type { Analytics } from 'firebase/analytics';

import { getFirebaseApp, getFirebaseConfig } from '@/services/firebase/config';

let analyticsInstance: Analytics | null = null;
let initPromise: Promise<Analytics | null> | null = null;

/**
 * Inicjalizuje Firebase Analytics, jeśli:
 * - Firebase jest skonfigurowany wraz z measurementId,
 * - środowisko przeglądarki wspiera Analytics (np. nie w SSR / niektórych webview).
 *
 * Zwraca null, gdy Analytics nie może zostać uruchomiony — wywołania pozostają wtedy no-op.
 */
export async function getAnalytics(): Promise<Analytics | null> {
  if (analyticsInstance) return analyticsInstance;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const config = getFirebaseConfig();
    if (!config?.measurementId) return null;

    const { isSupported, getAnalytics: getFirebaseAnalytics } = await import('firebase/analytics');
    if (!(await isSupported())) return null;

    const app = await getFirebaseApp();
    analyticsInstance = getFirebaseAnalytics(app);
    return analyticsInstance;
  })();

  return initPromise;
}

/** Loguje zdarzenie odsłony strony (SPA — wywoływane przy zmianie trasy). */
export async function logPageView(path: string, title?: string): Promise<void> {
  const analytics = await getAnalytics();
  if (!analytics) return;
  const { logEvent } = await import('firebase/analytics');
  logEvent(analytics, 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
}

/** Loguje dowolne zdarzenie niestandardowe. */
export async function logAnalyticsEvent(
  name: string,
  params?: Record<string, unknown>,
): Promise<void> {
  const analytics = await getAnalytics();
  if (!analytics) return;
  const { logEvent } = await import('firebase/analytics');
  logEvent(analytics, name, params);
}
